import {
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react"

import {
  PublicLookupSectionSkeleton,
  PublicSidebarSectionSkeleton,
} from "@/components/app/public-loading-skeleton"
import { Button } from "@/components/ui/button"
import { uploadOrchardObservationPhotos } from "@/features/public/orchard/uploadthing-client"
import {
  apiClient,
  type OrchardObservationCreateRequest,
  type OrchardObservationRecord,
  type OrchardTreeRecord,
} from "@/lib/api/client"

type ObservationType =
  | "flowering"
  | "fruit_set"
  | "fruit_load"
  | "harvest"
  | "health_check"

interface PublicOrchardTreePageProps {
  initialToken: string
}

interface OrchardObservationFormState {
  diseaseStatus: string
  estimatedFruitLoad: string
  floweringIntensity: string
  floweringPresent: string
  fruitSetPresent: string
  fruitSetQuality: string
  harvestRound: string
  healthStatus: string
  notes: string
  observedByName: string
  observationType: ObservationType
  pestStatus: string
  yieldQuantity: string
  yieldUnit: string
}

const SECTION_CLASS = "shell-panel rounded-4xl border border-border/70 p-5 sm:p-6"
const INPUT_CLASS =
  "mt-2 w-full rounded-2xl border border-border bg-background/90 px-4 py-3 text-sm shadow-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15"
const TEXTAREA_CLASS = `${INPUT_CLASS} min-h-28 resize-y`
const LABEL_CLASS = "text-sm font-medium text-foreground"
const HELP_CLASS = "mt-2 text-xs leading-5 text-muted-foreground"
const BANNER_BASE = "rounded-3xl border px-4 py-3 text-sm leading-6"
const ORCHARD_PHOTO_LIMIT = 4
const ORCHARD_PHOTO_MAX_FILE_SIZE = 6 * 1024 * 1024
const ORCHARD_PHOTO_MAX_TOTAL_BYTES = 24 * 1024 * 1024

const OBSERVATION_TYPE_OPTIONS: Array<{ description: string; label: string; value: ObservationType }> = [
  {
    description: "Record whether flowering has started and how strong it appears.",
    label: "Flowering",
    value: "flowering",
  },
  {
    description: "Capture fruit set progress after flowering.",
    label: "Fruit set",
    value: "fruit_set",
  },
  {
    description: "Log estimated fruit load before harvest.",
    label: "Fruit load",
    value: "fruit_load",
  },
  {
    description: "Save harvested quantity for the tree.",
    label: "Harvest",
    value: "harvest",
  },
  {
    description: "Track tree health, pests, disease, and treatment notes.",
    label: "Health check",
    value: "health_check",
  },
] as const

const YES_NO_OPTIONS = [
  { label: "Select", value: "" },
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
] as const

const YIELD_UNIT_OPTIONS = [
  { label: "kg", value: "kg" },
  { label: "crates", value: "crates" },
  { label: "boxes", value: "boxes" },
] as const

const INITIAL_FORM: OrchardObservationFormState = {
  diseaseStatus: "",
  estimatedFruitLoad: "",
  floweringIntensity: "",
  floweringPresent: "",
  fruitSetPresent: "",
  fruitSetQuality: "",
  harvestRound: "",
  healthStatus: "",
  notes: "",
  observedByName: "",
  observationType: "flowering",
  pestStatus: "",
  yieldQuantity: "",
  yieldUnit: "kg",
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-IN", {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  })
}

function parseOptionalFloat(value: string) {
  const normalized = value.trim()
  if (!normalized) {
    return null
  }

  const parsed = Number.parseFloat(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function parseOptionalInteger(value: string) {
  const normalized = value.trim()
  if (!normalized) {
    return null
  }

  const parsed = Number.parseInt(normalized, 10)
  return Number.isFinite(parsed) ? parsed : null
}

function parseOptionalBoolean(value: string) {
  if (!value) {
    return null
  }

  return value === "yes"
}

function isAllowedOrchardPhoto(file: File) {
  return new Set([
    "image/heic",
    "image/heif",
    "image/jpeg",
    "image/png",
    "image/webp",
  ]).has(file.type)
}

function getObservationTypeLabel(value: string | null | undefined) {
  return (
    OBSERVATION_TYPE_OPTIONS.find((option) => option.value === value)?.label
    || value
    || "Observation"
  )
}

function buildObservationPayload(
  form: OrchardObservationFormState,
  token: string
): OrchardObservationCreateRequest {
  return {
    diseaseStatus: form.diseaseStatus.trim() || null,
    estimatedFruitLoad: form.estimatedFruitLoad.trim() || null,
    floweringIntensity: form.floweringIntensity.trim() || null,
    floweringPresent: parseOptionalBoolean(form.floweringPresent),
    fruitSetPresent: parseOptionalBoolean(form.fruitSetPresent),
    fruitSetQuality: form.fruitSetQuality.trim() || null,
    harvestRound: parseOptionalInteger(form.harvestRound),
    healthStatus: form.healthStatus.trim() || null,
    notes: form.notes.trim() || null,
    observationType: form.observationType,
    observedByName: form.observedByName.trim() || null,
    pestStatus: form.pestStatus.trim() || null,
    qrToken: token,
    yieldQuantity: parseOptionalFloat(form.yieldQuantity),
    yieldUnit: form.yieldQuantity.trim() ? form.yieldUnit : null,
  }
}

function resetObservationForm(previous: OrchardObservationFormState) {
  return {
    ...INITIAL_FORM,
    observationType: previous.observationType,
    observedByName: previous.observedByName,
    yieldUnit: previous.yieldUnit,
  }
}

function TreeHeader({ tree }: { tree: OrchardTreeRecord }) {
  return (
    <div className={SECTION_CLASS}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary/80">
            GrowBetter Orchard Intelligence
          </p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            {tree.treeCode}
          </h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {tree.orchard?.name || "Orchard"}{tree.block?.name ? ` · ${tree.block.name}` : ""}
            {tree.row?.code ? ` · Row ${tree.row.code}` : ""}
            {tree.variety ? ` · ${tree.variety}` : ""}
          </p>
        </div>
        <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3 text-sm">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            QR token
          </div>
          <div className="mt-2 break-all font-medium">{tree.qrToken}</div>
        </div>
      </div>
    </div>
  )
}

export function PublicOrchardTreePage({
  initialToken,
}: PublicOrchardTreePageProps) {
  const [tree, setTree] = useState<OrchardTreeRecord | null>(null)
  const [history, setHistory] = useState<OrchardObservationRecord[]>([])
  const [form, setForm] = useState<OrchardObservationFormState>(INITIAL_FORM)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [submitMessage, setSubmitMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([])
  const photoInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    let isActive = true

    async function loadTreeBundle() {
      startTransition(() => {
        setIsLoading(true)
        setErrorMessage(null)
      })

      try {
        const [treeResult, historyResult] = await Promise.all([
          apiClient.lookupOrchardTreeByToken(initialToken),
          apiClient.getOrchardTreeHistoryByToken(initialToken, 8),
        ])

        if (!isActive) {
          return
        }

        startTransition(() => {
          setTree(treeResult.data)
          setHistory(historyResult.data ?? [])
          setIsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setTree(null)
          setHistory([])
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Unable to load this orchard tree."
          )
          setIsLoading(false)
        })
      }
    }

    void loadTreeBundle()

    return () => {
      isActive = false
    }
  }, [initialToken])

  const activeObservationType = useMemo(
    () =>
      OBSERVATION_TYPE_OPTIONS.find((option) => option.value === form.observationType)
      || OBSERVATION_TYPE_OPTIONS[0],
    [form.observationType]
  )

  function handlePhotoSelection(event: ChangeEvent<HTMLInputElement>) {
    const incomingFiles = Array.from(event.target.files ?? [])
    const nextFiles = incomingFiles.slice(0, ORCHARD_PHOTO_LIMIT)

    for (const file of nextFiles) {
      if (!isAllowedOrchardPhoto(file)) {
        setErrorMessage(
          "Only jpeg, png, webp, heic, and heif photos can be attached to an orchard observation."
        )
        return
      }

      if (file.size > ORCHARD_PHOTO_MAX_FILE_SIZE) {
        setErrorMessage(`${file.name} exceeds the 6 MB orchard-photo limit.`)
        return
      }
    }

    const totalBytes = nextFiles.reduce((sum, file) => sum + file.size, 0)
    if (totalBytes > ORCHARD_PHOTO_MAX_TOTAL_BYTES) {
      setErrorMessage("The selected orchard photos exceed the 24 MB total upload limit.")
      return
    }

    setErrorMessage(null)
    setSelectedPhotos(nextFiles)
  }

  async function submitObservation() {
    const payload = buildObservationPayload(form, initialToken)

    if (!payload.observationType) {
      setErrorMessage("Observation type is required.")
      return
    }

    startTransition(() => {
      setIsSubmitting(true)
      setErrorMessage(null)
      setSubmitMessage(selectedPhotos.length ? "Uploading orchard photos..." : "Saving orchard observation...")
      setSuccessMessage(null)
    })

    try {
      const uploadedPhotos = await uploadOrchardObservationPhotos({
        onStatusChange: setSubmitMessage,
        photos: selectedPhotos,
      })
      const finalPayload = uploadedPhotos.length
        ? {
            ...payload,
            photos: uploadedPhotos,
          }
        : payload
      const created = await apiClient.createOrchardObservation(finalPayload)
      const [treeResult, historyResult] = await Promise.all([
        apiClient.lookupOrchardTreeByToken(initialToken),
        apiClient.getOrchardTreeHistoryByToken(initialToken, 8),
      ])

      startTransition(() => {
        setTree(treeResult.data)
        setHistory(historyResult.data ?? [])
        setForm((current) => resetObservationForm(current))
        setSelectedPhotos([])
        setSubmitMessage(null)
        setSuccessMessage(
          `Saved ${getObservationTypeLabel(created.data.observationType)} for ${treeResult.data.treeCode}.`
        )
        setIsSubmitting(false)
      })
      if (photoInputRef.current) {
        photoInputRef.current.value = ""
      }
    } catch (error) {
      startTransition(() => {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to save orchard observation."
        )
        setSubmitMessage(null)
        setIsSubmitting(false)
      })
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void submitObservation()
  }

  if (isLoading) {
    return (
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.9fr)]">
        <div className="grid gap-6" data-testid="public-orchard-tree-skeleton">
          <PublicLookupSectionSkeleton data-testid="public-orchard-tree-skeleton" />
          <PublicLookupSectionSkeleton />
        </div>
        <div data-testid="public-orchard-tree-sidebar-skeleton">
          <PublicSidebarSectionSkeleton />
        </div>
      </div>
    )
  }

  if (!tree) {
    return (
      <section className={SECTION_CLASS}>
        <h1 className="text-2xl font-semibold tracking-tight">Tree lookup unavailable</h1>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          {errorMessage || "This orchard tree could not be found."}
        </p>
      </section>
    )
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.95fr)]">
      <div className="grid gap-6">
        <TreeHeader tree={tree} />

        {errorMessage ? (
          <div className={`${BANNER_BASE} border-destructive/35 bg-destructive/8 text-destructive`}>
            {errorMessage}
          </div>
        ) : null}
        {successMessage ? (
          <div className={`${BANNER_BASE} border-primary/25 bg-primary/6 text-foreground`}>
            {successMessage}
          </div>
        ) : null}
        {isSubmitting && submitMessage ? (
          <div className={`${BANNER_BASE} border-border/70 bg-background/70 text-foreground`}>
            {submitMessage}
          </div>
        ) : null}

        <section className={SECTION_CLASS}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Seasonal observation</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Record flowering, fruit set, fruit load, harvest, or health notes for this tree.
              </p>
            </div>
            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3 text-sm text-muted-foreground">
              {activeObservationType.description}
            </div>
          </div>

          <form className="mt-6 grid gap-5" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <label className={LABEL_CLASS}>
                Observation type
                <select
                  aria-label="Observation type"
                  className={INPUT_CLASS}
                  value={form.observationType}
                  onChange={(event) => {
                    setForm((current) => ({
                      ...current,
                      observationType: event.target.value as ObservationType,
                    }))
                  }}
                >
                  {OBSERVATION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className={LABEL_CLASS}>
                Observed by
                <input
                  aria-label="Observed by"
                  className={INPUT_CLASS}
                  value={form.observedByName}
                  onChange={(event) => {
                    setForm((current) => ({
                      ...current,
                      observedByName: event.target.value,
                    }))
                  }}
                />
              </label>
            </div>

            {form.observationType === "flowering" ? (
              <div className="grid gap-4 md:grid-cols-2">
                <label className={LABEL_CLASS}>
                  Flowering present
                  <select
                    aria-label="Flowering present"
                    className={INPUT_CLASS}
                    value={form.floweringPresent}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        floweringPresent: event.target.value,
                      }))
                    }}
                  >
                    {YES_NO_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={LABEL_CLASS}>
                  Flowering intensity
                  <input
                    aria-label="Flowering intensity"
                    className={INPUT_CLASS}
                    placeholder="Low / Medium / High"
                    value={form.floweringIntensity}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        floweringIntensity: event.target.value,
                      }))
                    }}
                  />
                </label>
              </div>
            ) : null}

            {form.observationType === "fruit_set" ? (
              <div className="grid gap-4 md:grid-cols-2">
                <label className={LABEL_CLASS}>
                  Fruit set present
                  <select
                    aria-label="Fruit set present"
                    className={INPUT_CLASS}
                    value={form.fruitSetPresent}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        fruitSetPresent: event.target.value,
                      }))
                    }}
                  >
                    {YES_NO_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={LABEL_CLASS}>
                  Fruit set quality
                  <input
                    aria-label="Fruit set quality"
                    className={INPUT_CLASS}
                    placeholder="Sparse / Balanced / Strong"
                    value={form.fruitSetQuality}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        fruitSetQuality: event.target.value,
                      }))
                    }}
                  />
                </label>
              </div>
            ) : null}

            {form.observationType === "fruit_load" ? (
              <label className={LABEL_CLASS}>
                Estimated fruit load
                <input
                  aria-label="Estimated fruit load"
                  className={INPUT_CLASS}
                  placeholder="Example: 35 fruits, medium load"
                  value={form.estimatedFruitLoad}
                  onChange={(event) => {
                    setForm((current) => ({
                      ...current,
                      estimatedFruitLoad: event.target.value,
                    }))
                  }}
                />
              </label>
            ) : null}

            {form.observationType === "harvest" ? (
              <div className="grid gap-4 md:grid-cols-3">
                <label className={LABEL_CLASS}>
                  Yield quantity
                  <input
                    aria-label="Yield quantity"
                    className={INPUT_CLASS}
                    inputMode="decimal"
                    value={form.yieldQuantity}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        yieldQuantity: event.target.value,
                      }))
                    }}
                  />
                </label>
                <label className={LABEL_CLASS}>
                  Yield unit
                  <select
                    aria-label="Yield unit"
                    className={INPUT_CLASS}
                    value={form.yieldUnit}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        yieldUnit: event.target.value,
                      }))
                    }}
                  >
                    {YIELD_UNIT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={LABEL_CLASS}>
                  Harvest round
                  <input
                    aria-label="Harvest round"
                    className={INPUT_CLASS}
                    inputMode="numeric"
                    value={form.harvestRound}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        harvestRound: event.target.value,
                      }))
                    }}
                  />
                </label>
              </div>
            ) : null}

            {form.observationType === "health_check" ? (
              <div className="grid gap-4 md:grid-cols-2">
                <label className={LABEL_CLASS}>
                  Health status
                  <input
                    aria-label="Health status"
                    className={INPUT_CLASS}
                    placeholder="Healthy / Weak / Stress visible"
                    value={form.healthStatus}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        healthStatus: event.target.value,
                      }))
                    }}
                  />
                </label>
                <label className={LABEL_CLASS}>
                  Pest status
                  <input
                    aria-label="Pest status"
                    className={INPUT_CLASS}
                    placeholder="No pest / Mild / Severe"
                    value={form.pestStatus}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        pestStatus: event.target.value,
                      }))
                    }}
                  />
                </label>
                <label className={LABEL_CLASS}>
                  Disease status
                  <input
                    aria-label="Disease status"
                    className={INPUT_CLASS}
                    placeholder="No disease / Spotting / Infection visible"
                    value={form.diseaseStatus}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        diseaseStatus: event.target.value,
                      }))
                    }}
                  />
                </label>
              </div>
            ) : null}

            <label className={LABEL_CLASS}>
              Notes
              <textarea
                aria-label="Notes"
                className={TEXTAREA_CLASS}
                placeholder="Optional field note, treatment note, or seasonal remark."
                value={form.notes}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }}
              />
              <p className={HELP_CLASS}>
                Keep this short. The main goal is fast seasonal logging from the field.
              </p>
            </label>

            <label className={LABEL_CLASS}>
              Observation photos
              <input
                ref={photoInputRef}
                aria-label="Observation photos"
                className={INPUT_CLASS}
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                type="file"
                onChange={handlePhotoSelection}
              />
              <p className={HELP_CLASS}>
                Optional. Up to {ORCHARD_PHOTO_LIMIT} photos, 6 MB each, 24 MB total.
              </p>
            </label>

            {selectedPhotos.length ? (
              <div className="grid gap-3 rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Selected photos</div>
                <div className="grid gap-2">
                  {selectedPhotos.map((photo) => (
                    <div
                      key={`${photo.name}-${photo.lastModified}`}
                      className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground"
                    >
                      {photo.name} · {Math.max(1, Math.round(photo.size / 1024))} KB
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex justify-end">
              <Button
                disabled={isSubmitting}
                type="button"
                onClick={() => {
                  void submitObservation()
                }}
              >
                {isSubmitting ? "Saving observation…" : "Save observation"}
              </Button>
            </div>
          </form>
        </section>
      </div>

      <div className="grid gap-6">
        <section className={SECTION_CLASS}>
          <h2 className="text-lg font-semibold tracking-tight">Tree context</h2>
          <div className="mt-4 grid gap-3 text-sm">
            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Orchard
              </div>
              <div className="mt-2 font-medium">{tree.orchard?.name || "Not set"}</div>
            </div>
            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Block / Row
              </div>
              <div className="mt-2 font-medium">
                {tree.block?.code || "No block"}{tree.row?.code ? ` · ${tree.row.code}` : ""}
              </div>
            </div>
            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Last observation
              </div>
              <div className="mt-2 font-medium">
                {tree.latestObservation?.observedAt
                  ? formatDateTime(tree.latestObservation.observedAt)
                  : "No seasonal record yet"}
              </div>
              <div className="mt-2 text-muted-foreground">
                {tree.latestObservation
                  ? getObservationTypeLabel(tree.latestObservation.observationType)
                  : "Start with the first flowering, fruit, harvest, or health note."}
              </div>
              {tree.latestObservation?.photos?.length ? (
                <div className="mt-2 text-xs text-muted-foreground">
                  {tree.latestObservation.photos.length} photo(s) attached
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className={SECTION_CLASS}>
          <h2 className="text-lg font-semibold tracking-tight">Recent history</h2>
          {history.length ? (
            <div className="mt-4 grid gap-3">
              {history.map((entry) => (
                <div
                  key={entry.id}
                  className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">
                        {getObservationTypeLabel(entry.observationType)}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {formatDateTime(entry.observedAt)}
                      </div>
                    </div>
                    {entry.observedByName ? (
                      <div className="text-xs text-muted-foreground">
                        {entry.observedByName}
                      </div>
                    ) : null}
                  </div>
                  {entry.yieldQuantity !== undefined && entry.yieldQuantity !== null ? (
                    <div className="mt-3 text-sm text-muted-foreground">
                      Yield: {entry.yieldQuantity} {entry.yieldUnit || ""}
                    </div>
                  ) : null}
                  {entry.estimatedFruitLoad ? (
                    <div className="mt-3 text-sm text-muted-foreground">
                      Fruit load: {entry.estimatedFruitLoad}
                    </div>
                  ) : null}
                  {entry.notes ? (
                    <div className="mt-3 text-sm leading-6 text-muted-foreground">
                      {entry.notes}
                    </div>
                  ) : null}
                  {entry.photos?.length ? (
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {entry.photos.map((photo) => (
                        <a
                          key={photo.id}
                          className="overflow-hidden rounded-2xl border border-border/70 bg-background/70"
                          href={photo.url}
                          rel="noreferrer"
                          target="_blank"
                        >
                          <img
                            alt={`${getObservationTypeLabel(entry.observationType)} evidence`}
                            className="aspect-[4/3] w-full object-cover"
                            loading="lazy"
                            src={photo.url}
                          />
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm leading-6 text-muted-foreground">
              No observations recorded for this tree yet.
            </p>
          )}
        </section>
      </div>
    </div>
  )
}
